import { Download, TrendingDown, TrendingFlat } from "@mui/icons-material";
import {
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import React, { useMemo, useState } from "react";
import { useCurrencyFormatter } from "../hooks/useCurrencyFormatter";
import MortgageSimulator from "../simulator";

type Column = {
  id: string;
  label?: string;
  align?: "left" | "right" | "center";
  accessor?: (row: any, rowIndex: number) => any;
  formatter?: (value: any, row: any, rowIndex: number) => React.ReactNode;
  visible?: boolean;
};

type ColumnGroup = {
  label?: string | null;
  columns: Column[];
};

const reason: Record<string, string> = {
  "spread-additional-capital-repayments-over-deal": "Spread over deal term",
  "saved-maximum-additional-capital-repayment-amount":
    "Saved maximum ACR amount",
  "use-additional-capital-repayment-allowance-before-year-end":
    "Use ACR allowance before year end",
  "between-fixed-deals": "Between fixed deals",
};

export default function SimulationSchedule({
  sim,
}: {
  sim: MortgageSimulator;
}) {
  const c = useCurrencyFormatter();

  const hasSavings = Object.keys(sim.savingsTransactions).length > 0;

  const savingsTransactions = sim.savingsTransactions;

  const rows: any[] = sim.mortgage.ledger.entries.map(
    (entry: any, index: number) => {
      const added = Object.values(savingsTransactions).reduce(
        (acc: number, arr: any[]) => {
          const v =
            arr && arr[index] && typeof arr[index].amountAdded === "number"
              ? arr[index].amountAdded
              : 0;
          return acc + v;
        },
        0,
      );
      const interestAdded = Object.values(savingsTransactions).reduce(
        (acc: number, arr: any[]) => {
          const v =
            arr && arr[index] && typeof arr[index].interestAdded === "number"
              ? arr[index].interestAdded
              : 0;
          return acc + v;
        },
        0,
      );
      const balanceAfter = Object.values(savingsTransactions).reduce(
        (acc: number, arr: any[]) => {
          const v =
            arr && arr[index] && typeof arr[index].balanceAfter === "number"
              ? arr[index].balanceAfter
              : 0;
          return acc + v;
        },
        0,
      );

      return {
        key: entry.name ?? `row-${index}`,
        month: entry.month,
        principal: entry.principal,
        interestAmount: entry.interestAmount,
        monthlyPayment: entry.monthlyPayment,
        savingsAdded: added,
        savingsInterestAdded: interestAdded,
        savingsBalance: balanceAfter,
        additionalCapitalRepayments: entry.additionalCapitalRepayments,
        additionalCapitalRepaymentReason:
          entry.additionalCapitalRepaymentReason,
        type: entry.type,
        isFinal: false,
        dealId: entry.dealId,
      };
    },
  );

  rows.push({
    key: "final",
    month: "final",
    principal: sim.mortgage.principal,
    interestAmount: undefined,
    monthlyPayment: undefined,
    savingsAdded: undefined,
    savingsInterestAdded: undefined,
    savingsBalance: undefined,
    additionalCapitalRepayments: undefined,
    additionalCapitalRepaymentReason: undefined,
    type: undefined,
    isFinal: true,
    dealId: undefined,
  });

  const [grouping, setGrouping] = useState<"monthly" | "yearly" | "by-deal">(
    "yearly",
  );

  const groupRowsByYear = (inputRows: any[]) => {
    const map = new Map<number, any>();
    inputRows.forEach((row) => {
      if (row.isFinal) return;
      const monthNum = typeof row.month === "number" ? row.month : NaN;
      const key = isNaN(monthNum) ? 0 : Math.floor((monthNum - 1) / 12) + 1;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, {
          key: `year-${key}`,
          month: `Year ${key}`,
          months: 1,
          principal: row.principal ?? 0,
          interestAmount: row.interestAmount ?? 0,
          monthlyPayment: row.monthlyPayment ?? 0,
          savingsAdded: row.savingsAdded ?? 0,
          savingsInterestAdded: row.savingsInterestAdded ?? 0,
          savingsBalance: row.savingsBalance ?? 0,
          additionalCapitalRepayments: row.additionalCapitalRepayments ?? 0,
          additionalCapitalRepaymentReason: null,
          type: null,
          isFinal: false,
        });
      } else {
        existing.months += 1;
        // keep existing.principal as the start-of-period value (do not sum)
        existing.interestAmount += row.interestAmount ?? 0;
        existing.monthlyPayment += row.monthlyPayment ?? 0;
        existing.savingsAdded += row.savingsAdded ?? 0;
        existing.savingsInterestAdded += row.savingsInterestAdded ?? 0;
        // For balance take the last non-null value (end of period)
        existing.savingsBalance = row.savingsBalance ?? existing.savingsBalance;
        existing.additionalCapitalRepayments +=
          row.additionalCapitalRepayments ?? 0;
      }
    });
    return Array.from(map.values());
  };

  const groupRowsByDeal = (inputRows: any[]) => {
    const map = new Map<any, any>();
    inputRows.forEach((row) => {
      if (row.isFinal) return;
      const key = row.dealId ?? "no-deal";
      const existing = map.get(key);
      if (!existing) {
        map.set(key, {
          key: `deal-${key}`,
          month: row.dealId ? `Deal ${row.dealId}` : "No Deal",
          months: 1,
          principal: row.principal ?? 0,
          interestAmount: row.interestAmount ?? 0,
          monthlyPayment: row.monthlyPayment ?? 0,
          savingsAdded: row.savingsAdded ?? 0,
          savingsInterestAdded: row.savingsInterestAdded ?? 0,
          savingsBalance: row.savingsBalance ?? 0,
          additionalCapitalRepayments: row.additionalCapitalRepayments ?? 0,
          additionalCapitalRepaymentReason: null,
          type: row.type,
          isFinal: false,
        });
      } else {
        existing.months += 1;
        // keep existing.principal as the start-of-period value (do not sum)
        existing.interestAmount += row.interestAmount ?? 0;
        existing.monthlyPayment += row.monthlyPayment ?? 0;
        existing.savingsAdded += row.savingsAdded ?? 0;
        existing.savingsInterestAdded += row.savingsInterestAdded ?? 0;
        existing.savingsBalance = row.savingsBalance ?? existing.savingsBalance;
        existing.additionalCapitalRepayments +=
          row.additionalCapitalRepayments ?? 0;
      }
    });
    return Array.from(map.values());
  };

  const rowsToRender = useMemo(() => {
    if (grouping === "monthly") return rows;
    let grouped: any[] = [];
    if (grouping === "yearly") grouped = groupRowsByYear(rows);
    if (grouping === "by-deal") grouped = groupRowsByDeal(rows);
    const finalRow = rows.find((r) => r.isFinal);
    if (finalRow) grouped.push(finalRow);
    return grouped;
  }, [grouping, rows]);

  const columnGroups: ColumnGroup[] = useMemo(() => {
    const timeLabel = grouping === "monthly" ? "Month" : "";
    const principalLabel =
      grouping === "monthly" ? "Principal" : "Principal (start of period)";
    const interestLabel =
      grouping === "monthly" ? "Interest" : "Interest (total)";
    const paymentLabel =
      grouping === "monthly" ? "Monthly Payment" : "Payments (total)";
    const savingsAddedLabel =
      grouping === "monthly" ? "Added" : "Added (total)";
    const savingsInterestLabel =
      grouping === "monthly" ? "Interest" : "Interest (total)";
    const savingsBalanceLabel =
      grouping === "monthly" ? "Balance" : "Balance (end of period)";
    const overpaymentLabel =
      grouping === "monthly" ? "Overpayment" : "Overpayment (total)";
    const typeLabel = grouping === "yearly" ? "" : "Type";

    return [
      {
        label: null,
        columns: [
          {
            id: "month",
            label: timeLabel,
            accessor: (row) => row.month,
            formatter: (val, row) => {
              if (row.isFinal) {
                return sim.mortgage.principal > 0 ? (
                  <Chip color="error" label="Remaining balance" size="small" />
                ) : (
                  <Chip color="success" label="Paid off" size="small" />
                );
              }
              if (
                typeof val === "number" &&
                grouping === "monthly" &&
                val % 12 === 1
              ) {
                return (
                  <span>
                    {val} <em>(Year&nbsp;{Math.floor((val - 1) / 12) + 1})</em>
                  </span>
                );
              }
              return val;
            },
          },
        ],
      },
      {
        label: "Mortgage",
        columns: [
          {
            id: "principal",
            label: principalLabel,
            align: "right",
            accessor: (row) => row.principal,
            formatter: (val, row) => {
              if (row.isFinal) {
                return <strong>{c(val)}</strong>;
              }
              return val !== undefined ? c(val) : "";
            },
          },
          {
            id: "interestAmount",
            label: interestLabel,
            align: "right",
            accessor: (row) => row.interestAmount,
            formatter: (val) => (val !== undefined ? c(val) : ""),
          },
          {
            id: "monthlyPayment",
            label: paymentLabel,
            align: "right",
            accessor: (row) => row.monthlyPayment,
            formatter: (val) => (val !== undefined ? c(val) : ""),
          },
        ],
      },
      {
        label: "Savings & Investments",
        columns: [
          {
            id: "savingsAdded",
            label: savingsAddedLabel,
            align: "right",
            accessor: (row) => row.savingsAdded,
            formatter: (val) => (val !== undefined ? c(val) : ""),
            visible: hasSavings,
          },
          {
            id: "savingsInterestAdded",
            label: savingsInterestLabel,
            align: "right",
            accessor: (row) => row.savingsInterestAdded,
            formatter: (val) => (val !== undefined ? c(val) : ""),
            visible: hasSavings,
          },
          {
            id: "savingsBalance",
            label: savingsBalanceLabel,
            align: "right",
            accessor: (row) => row.savingsBalance,
            formatter: (val) => (val !== undefined ? c(val) : ""),
            visible: hasSavings,
          },
        ],
      },
      {
        label: null,
        columns: [
          {
            id: "additionalCapitalRepayments",
            label: overpaymentLabel,
            align: "right",
            accessor: (row) => row.additionalCapitalRepayments,
            formatter: (val, row) =>
              val > 0 ? (
                <Tooltip title={reason[row.additionalCapitalRepaymentReason]}>
                  <span>{c(val)}</span>
                </Tooltip>
              ) : (
                ""
              ),
            visible: sim.additionalCapitalRepayments,
          },
          {
            id: "type",
            label: typeLabel,
            align: "right",
            accessor: (row) => row.type,
            formatter: (val) =>
              val === "interest-only" ? (
                <Tooltip title="Interest only">
                  <TrendingFlat />
                </Tooltip>
              ) : val === "repayment" ? (
                <Tooltip title="Repayment (capital + interest)">
                  <TrendingDown />
                </Tooltip>
              ) : (
                ""
              ),
          },
        ],
      },
    ];
  }, [grouping, hasSavings, sim, c]);

  const visibleColumnCountForGroup = (group: ColumnGroup) =>
    group.columns.reduce(
      (acc, col) => acc + (col.visible === false ? 0 : 1),
      0,
    );

  const flattenedVisibleColumns: Column[] = columnGroups.reduce<Column[]>(
    (acc, group) => {
      group.columns.forEach((col) => {
        if (col.visible !== false) acc.push(col);
      });
      return acc;
    },
    [],
  );

  const exportToCsv = () => {
    const hasOverpaymentColumn = flattenedVisibleColumns.some(
      (col) => col.id === "additionalCapitalRepayments",
    );
    const headers = flattenedVisibleColumns.flatMap((col) => {
      const label = col.label ?? col.id;
      if (col.id === "additionalCapitalRepayments" && hasOverpaymentColumn) {
        return [label, "Overpayment Reason"];
      }
      return [label];
    });
    const csvRows = [headers.join(",")];
    rowsToRender.forEach((row, rowIndex) => {
      const values = flattenedVisibleColumns.flatMap((col) => {
        const raw = col.accessor ? col.accessor(row, rowIndex) : row[col.id];
        if (col.id === "month" && row.isFinal) {
          const label =
            sim.mortgage.principal > 0 ? "Remaining balance" : "Paid off";
          return [label];
        }
        if (col.id === "type") {
          if (raw === "interest-only") return ["Interest only"];
          if (raw === "repayment") return ["Repayment"];
          return [""];
        }
        if (col.id === "additionalCapitalRepayments" && hasOverpaymentColumn) {
          const reasonText =
            raw > 0 && row.additionalCapitalRepaymentReason
              ? (reason[row.additionalCapitalRepaymentReason] ?? "")
              : "";
          return [raw > 0 ? raw : "", reasonText];
        }
        if (raw === undefined || raw === null) return [""];
        return [raw];
      });
      csvRows.push(
        values
          .map((v) => {
            const s = String(v);
            return s.includes(",") || s.includes('"') || s.includes("\n")
              ? `"${s.replace(/"/g, '""')}"`
              : s;
          })
          .join(","),
      );
    });
    const blob = new Blob([csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `simulation-schedule-${grouping}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Simulation Schedule
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small">
            <InputLabel id="grouping-select-label">Grouping</InputLabel>
            <Select
              labelId="grouping-select-label"
              value={grouping}
              label="Grouping"
              onChange={(e) => setGrouping(e.target.value as any)}
            >
              <MenuItem value={"monthly"}>Monthly</MenuItem>
              <MenuItem value={"yearly"}>Yearly</MenuItem>
              {sim.additionalCapitalRepayments && (
                <MenuItem value={"by-deal"}>By deal</MenuItem>
              )}
            </Select>
          </FormControl>
          <Tooltip title={"Export to CSV"} arrow={true}>
            <IconButton
              onClick={exportToCsv}
              aria-label="Export schedule to CSV"
              sx={{
                border: 1,
                borderColor: "rgba(0, 0, 0, 0.23)",
                borderRadius: 1,
                width: 40,
                height: 40,
                color: "action.active",
                "&:hover": {
                  borderColor: "text.primary",
                  backgroundColor: "transparent",
                },
              }}
            >
              <Download />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
          <TableHead>
            {visibleColumnCountForGroup(columnGroups[2]) > 0 && (
              <TableRow>
                {columnGroups.map((group, gi) => {
                  const span = visibleColumnCountForGroup(group);
                  if (span === 0) return null;
                  return (
                    <TableCell
                      key={`g-${gi}`}
                      align="center"
                      colSpan={span}
                      sx={{
                        borderRight: group.label
                          ? "1px solid rgba(224, 224, 224, 1)"
                          : "none",
                      }}
                    >
                      <strong>{group.label ?? ""}</strong>
                    </TableCell>
                  );
                })}
              </TableRow>
            )}
            <TableRow>
              {columnGroups.map((group) =>
                group.columns.map((col, ci) =>
                  col.visible === false ? null : (
                    <TableCell
                      key={col.id}
                      align={col.align ?? "left"}
                      sx={{
                        borderRight:
                          visibleColumnCountForGroup(columnGroups[2]) > 0 &&
                          group.label &&
                          ci ===
                            group.columns.filter((c) => c.visible !== false)
                              .length -
                              1
                            ? "1px solid rgba(224, 224, 224, 1)"
                            : "none",
                      }}
                    >
                      <strong>{col.label}</strong>
                    </TableCell>
                  ),
                ),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsToRender.map((row, rowIndex) => (
              <TableRow
                key={row.key}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                {flattenedVisibleColumns.map((col, colIndex) => {
                  const raw = col.accessor
                    ? col.accessor(row, rowIndex)
                    : row[col.id];
                  const cell = col.formatter
                    ? col.formatter(raw, row, rowIndex)
                    : (raw ?? "");

                  // Find which group this column belongs to and if it's the last column in that group
                  let isLastInGroup = false;
                  let belongsToLabeledGroup = false;
                  let cumulativeIndex = 0;

                  for (const group of columnGroups) {
                    const visibleCols = group.columns.filter(
                      (c) => c.visible !== false,
                    );
                    const groupSize = visibleCols.length;

                    if (
                      colIndex >= cumulativeIndex &&
                      colIndex < cumulativeIndex + groupSize
                    ) {
                      belongsToLabeledGroup = !!group.label;
                      isLastInGroup =
                        colIndex === cumulativeIndex + groupSize - 1;
                      break;
                    }
                    cumulativeIndex += groupSize;
                  }

                  return (
                    <TableCell
                      key={`${row.key}-${col.id}`}
                      align={col.align ?? "left"}
                      sx={{
                        borderRight:
                          visibleColumnCountForGroup(columnGroups[2]) > 0 &&
                          belongsToLabeledGroup &&
                          isLastInGroup
                            ? "1px solid rgba(224, 224, 224, 1)"
                            : "none",
                      }}
                    >
                      {cell}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
